import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A sticky "Select options" button for mobile PDP that opens the native
 * quick view modal for the current product. Reuses Horizon's quick-add-dialog
 * for full variant selection with zero custom UI.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} buttonBar - The sticky button bar
 * @property {HTMLButtonElement} triggerButton - The select options button
 *
 * @extends {Component<Refs>}
 */
class StickySelectOptions extends Component {
  /** @type {IntersectionObserver | null} */
  #buyButtonsObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {boolean} */
  #hiddenByFooter = false;

  /** @type {Element | null} */
  #cachedContent = null;

  /** @type {HTMLElement | null} */
  #buyButtonsBlock = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setupIntersectionObserver();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsObserver?.disconnect();
    this.#footerObserver?.disconnect();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * Resets stuck state after cart update, since the page may morph/reset
   * and the buy buttons could be back in view.
   */
  #handleCartUpdate = () => {
    this.refs.triggerButton.classList.remove('sticky-select-options__button--loading');
    this.#hide();
  };

  #setupIntersectionObserver() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const section = document.getElementById(`shopify-section-${sectionId}`);
    this.#buyButtonsBlock = section?.querySelector('.buy-buttons-block');
    if (!this.#buyButtonsBlock) return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    this.#buyButtonsObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          this.#show();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByFooter = false;
        this.#hide();
      }
    });

    this.#buyButtonsObserver.observe(this.#buyButtonsBlock);

    if (footer) {
      this.#footerObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;

          if (entry.isIntersecting && this.#isStuck) {
            this.#hiddenByFooter = true;
            this.#hide();
          } else if (!entry.isIntersecting && this.#hiddenByFooter) {
            const rect = this.#buyButtonsBlock.getBoundingClientRect();
            if (rect.bottom < 0 || rect.top < 0) {
              this.#hiddenByFooter = false;
              this.#show();
            }
          }
        },
        { rootMargin: '200px 0px 0px 0px' }
      );
      this.#footerObserver.observe(footer);
    }
  }

  #show() {
    this.#isStuck = true;
    // Query DOM directly — refs may be stale after section morph
    this.querySelector('.sticky-select-options__button')?.classList.remove('sticky-select-options__button--loading');
    this.refs.buttonBar.dataset.stuck = 'true';
  }

  #hide() {
    this.#isStuck = false;
    this.refs.buttonBar.dataset.stuck = 'false';
  }

  /**
   * Opens the quick view modal for the current product.
   */
  async openQuickView() {
    const productUrl = this.dataset.productUrl;
    if (!productUrl) return;

    if (!this.#cachedContent) {
      this.refs.triggerButton.classList.add('sticky-select-options__button--loading');

      try {
        const response = await fetch(productUrl);
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const gridElement = doc.querySelector('[data-product-grid-content]');

        if (gridElement) {
          this.#cachedContent = /** @type {Element} */ (gridElement.cloneNode(true));
        }
      } catch (error) {
        console.error('Failed to fetch product page:', error);
      } finally {
        this.refs.triggerButton.classList.remove('sticky-select-options__button--loading');
      }
    }

    if (!this.#cachedContent) return;

    const modalContent = document.getElementById('quick-add-modal-content');
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!modalContent || !dialogComponent) return;

    // Clone cached content and prepare for modal
    const freshContent = /** @type {Element} */ (this.#cachedContent.cloneNode(true));
    this.#prepareModalContent(freshContent, modalContent);

    // Open the quick view dialog
    if (typeof dialogComponent.showDialog === 'function') {
      dialogComponent.showDialog();
    }
  }

  /**
   * Prepares and morphs content into the quick view modal.
   * Follows the same mobile restructuring pattern as QuickAddComponent.
   *
   * @param {Element} productGrid - The fetched product grid content
   * @param {HTMLElement} modalContent - The modal content container
   */
  #prepareModalContent(productGrid, modalContent) {
    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.dataset.productUrl || '';

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);
  }
}

if (!customElements.get('sticky-select-options')) {
  customElements.define('sticky-select-options', StickySelectOptions);
}
